import React, { useContext, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from '../../../../context/translationProvider'
import { Field } from '../../field'
import { emailRegex } from '../../../../utils/emailValidation'
import {
  validateNationalId,
  validatePhoneNumber,
  validateUUID,
} from '../../../../helpers/validators'
import { AuthContext } from '../../../../context/authProvider'
import {
  BR_MOBILE_MASK,
  formatMobileNumber,
  removePhoneMask,
} from '../../../../helpers/mask'
import useDeviceDetect from '../../../../hooks/useDeviceDetect'
import {
  createPixPingBankAccount,
  getLivenessStatus,
  requestLivenessPixPing,
} from '../../../../adapters/payments'
import { toast } from '../../../atoms'
import CafPixPingModal from '../cafPixPingModal'
import { Text, Title } from '../../../atoms/text'
import { If } from '../../../atoms/if'
import { Message } from '../../../atoms/message'
import { Checkbox } from '../../../atoms/checkbox'
import { Button } from '../../../atoms/button'
import { SpinnerLoader } from '../../../atoms/loader'
import PropTypes from 'prop-types'
import { STEPS } from '../../../organisms/deposit/utils'
import {
  ArrowBackPixPingContainer,
  ConfirmPixStepBody,
  DepositPixPingTitle,
  StyledInformationAlert,
} from '../styles'
import { Image } from '../../../atoms/image'
import InformationIcon from '../../../../images/icon_information.svg'
import ArrowLeftIcon from '../../../../images/pix-ping/arrow_left.svg'
import { handlePixError } from './utils'
import { CAF_MESSAGE_CODES, EVENT_LOCATIONS, EVENT_NAMES, FIVE_SECONDS } from '../../../../utils/constants'

const ArrowBackPixPing = (props) => {
  const { setCurrentStep, isMobile } = props
  const { translate } = useTranslation()

  if (isMobile) return null

  return (
    <ArrowBackPixPingContainer onClick={() => setCurrentStep(STEPS.SELECT_PIX)}>
      <div>
        <Image url={ArrowLeftIcon} width={15} height={15} />
      </div>
      <div style={{ marginTop: '8.5px' }}>
        <Text>{translate('deposit.pixPing.title')}</Text>
      </div>
    </ArrowBackPixPingContainer>
  )
}

ArrowBackPixPing.propTypes = {
  setCurrentStep: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
}

const ConfirmPixStep = (props) => {
  const { 
    selectedPixKey, 
    openDepositFlowFromPixPing, 
    setCurrentStep,
    trackEvent,
    eventPixType 
  } = props

  const { user } = useContext(AuthContext)
  const [livenessPixPingData, setLivenessPixPingData] = useState(null)
  const [isCafPixPingOpen, setIsCafPixPingOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false)
  const [repeatingTimeoutId, setRepeatingTimeoutId] = useState()
  const timeoutIdRef = useRef(repeatingTimeoutId)
  timeoutIdRef.current = repeatingTimeoutId

  const checkIsVerified = async () => {
    const response = await getLivenessStatus(livenessPixPingData.onboardingId)

    if (!response.ok) {
      toast.error(response.error.message)
      clearTimeout(timeoutIdRef.current)
      handleCloseCafPixPing()
      return
    }

    if (response.data.status === 'APPROVED') {
      clearTimeout(timeoutIdRef.current)
      handleCloseCafPixPing()
      handleCreatePixPingBankAccount()
      return
    }

    if (response.data.status === 'REPROVED') {
      clearTimeout(timeoutIdRef.current)
      handleCloseCafPixPing()
      toast.error(translate('payments.deposit.generalError'))
      return
    }

    const newTimeoutId = setTimeout(() => checkIsVerified(), FIVE_SECONDS)
    setRepeatingTimeoutId(newTimeoutId)
  }

  const handleCloseCafPixPing = () => {
    setIsCafPixPingOpen(false)
  }

  const formProperties = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      national_id: user?.national_id,
      email: user?.email,
      mobileNumber: formatMobileNumber(user?.mobile_number),
      randomKey: '',
    },
  })

  const { isMobile } = useDeviceDetect()

  const { translate } = useTranslation()

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
  } = formProperties

  const country = {
    id: 31,
    national_id_required: true,
    national_id_mask: '999.999.999-99',
    national_id_label: 'CPF',
  }

  const getLivenessForPixPing = async () => {
    setIsLoading(true)

    const {
      data: { url, onboardingId },
    } = await requestLivenessPixPing()

    if (url && onboardingId) {
      setLivenessPixPingData({ url, onboardingId })
      setIsLoading(false)

      trackEvent(EVENT_NAMES.SCREEN_LOADED, {
        screen_name: EVENT_LOCATIONS.LIVENESS_START,
        liveness_reason: 'Ping Pix',
        pix_key_type: eventPixType,
      }, true)

      setIsCafPixPingOpen(true)
    }
  }

  const onSubmit = async () => {
    getLivenessForPixPing()
  }

  const handleFinishLivenessPixPing = (messageCode) => {
    if (messageCode === CAF_MESSAGE_CODES.INVALID) {
      return
    }

    checkIsVerified()

    if (messageCode === CAF_MESSAGE_CODES.FINISH_FACE_AUTH) {
      trackEvent(EVENT_NAMES.LIVENESS_COMPLETED, {
        liveness_reason: 'Ping Pix',
        pix_key_type: eventPixType,
        user_id: user?.id,
      }, true)
    }
  }

  const renderPixPingData = () => {
    switch (selectedPixKey) {
      case 1:
        return {
          pixKey: getValues('national_id'),
          keyType: 'CPF',
        }
      case 2:
        return {
          pixKey: getValues('email'),
          keyType: 'Email',
        }
      case 3:
        return {
          pixKey: `55${removePhoneMask(getValues('mobileNumber'))}`,
          keyType: 'Phone',
        }
      case 4:
        return {
          pixKey: getValues('randomKey'),
          keyType: 'EVP',
        }
      default:
        return null
    }
  }

  const handleCreatePixPingBankAccount = async () => {
    const pixPingBodyData = renderPixPingData()

    const model = {
      ...pixPingBodyData,
      onboardingId: livenessPixPingData?.onboardingId,
    }

    const response = await createPixPingBankAccount(model)

    if (response.ok) {
      toast.success(translate('deposit.pixPing.success.bankAccountCreated'))
      openDepositFlowFromPixPing()
      window.postMessage('bankAccountCreated', '*')
    } else {
      const errorMessage = handlePixError(response.error, translate)
      toast.error(errorMessage)
    }
  }

  const isDisabled = !isCheckboxChecked || !isValid || isLoading

  return (
    <>
      <ArrowBackPixPing setCurrentStep={setCurrentStep} isMobile={isMobile} />
      <CafPixPingModal
        cafIframeUrl={livenessPixPingData?.url}
        isOpen={isCafPixPingOpen}
        onClose={handleCloseCafPixPing}
        closeCafIFrame={handleFinishLivenessPixPing}
        startAltDescription={translate('deposit.pixPing.liveness.description')}
      />
      <DepositPixPingTitle>
        <Title level={3} style={{ fontSize: '25px' }}>
          {translate('deposit.pixPing.confirm.title')}
        </Title>
        <Text
          tagType="p"
          style={{ marginTop: '10px', fontSize: '16.5px', opacity: 0.5 }}
        >
          {translate('deposit.pixPing.confirm.description')}
        </Text>
      </DepositPixPingTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ConfirmPixStepBody>
          <div>
            <If
              condition={selectedPixKey === 1}
              render={() => (
                <Field
                  disabled={true}
                  autoCompleteOff
                  showErrorMsg
                  hideSymbols={true}
                  type={country?.national_id_mask}
                  label={country?.national_id_label}
                  mask={country?.national_id_mask}
                  placeholder={country?.national_id_mask}
                  errors={errors}
                  defaultValue={user?.national_id}
                  formProperties={{
                    ...register('national_id', {
                      required: true,
                      validate: (value) =>
                        validateNationalId(
                          value,
                          country,
                          translate('cpf.register.invalidFormat')
                        ),
                    }),
                  }}
                  renderError={
                    errors.national_id?.message &&
                    (() => (
                      <Message
                        error
                        style={{ marginBottom: '1em' }}
                        text={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: errors.national_id?.message,
                            }}
                          ></span>
                        }
                      />
                    ))
                  }
                />
              )}
            />
            <If
              condition={selectedPixKey === 2}
              render={() => (
                <Field
                  label={translate('userProfile.email')}
                  maxLength="100"
                  type="email"
                  autoCompleteOff
                  showErrorMsg
                  errors={errors}
                  hideSymbols={true}
                  defaultValue={user?.email}
                  formProperties={register('email', {
                    required: true,
                    pattern: {
                      value: emailRegex,
                      message: translate('register.error.incorrectEmailFormat'),
                    },
                  })}
                />
              )}
            />

            <If
              condition={selectedPixKey === 3}
              render={() => (
                <Field
                  mask={BR_MOBILE_MASK}
                  label={translate('deposit.pixPing.mobile.label')}
                  autoCompleteOff
                  type="tel"
                  errors={errors}
                  defaultValue={formatMobileNumber(user?.mobile_number)}
                  hideSymbols={true}
                  showErrorMsg
                  formProperties={register('mobileNumber', {
                    required: true,
                    validate: (value) =>
                      validatePhoneNumber(value, '+55') ||
                      translate('deposit.pixPing.mobile.invalid'),
                  })}
                />
              )}
            />

            <If
              condition={selectedPixKey === 4}
              render={() => (
                <Field
                  label={translate('deposit.pixPing.randomKey.label')}
                  autoCompleteOff
                  type="text"
                  errors={errors}
                  hideSymbols={true}
                  showErrorMsg
                  defaultValue={getValues('randomKey')}
                  formProperties={register('randomKey', {
                    required: true,
                    validate: (value) =>
                      validateUUID(value) ||
                      translate('deposit.pixPing.randomKey.invalid'),
                  })}
                  maxLength={36}
                />
              )}
            />

            <div style={{ marginTop: '6px' }}>
              <Checkbox
                required
                formProperties={register('authPixPing')}
                onClick={(e) => setIsCheckboxChecked(e.target.checked)}
                id={'authPixPing'}
              >
                <div>
                  <Text
                    tagType="p"
                    textAlign="left"
                    small
                    style={{ fontSize: '13px' }}
                  >
                    {translate('deposit.pixPing.authorization.text')}
                  </Text>
                </div>
              </Checkbox>
            </div>
          </div>
          <div>
            <StyledInformationAlert>
              <div>
                <Image url={InformationIcon} />
              </div>
              <div>
                <div style={{ lineHeight: '1.3' }}>
                  <strong>
                    {translate('deposit.pixPing.attention.title')}
                  </strong>
                </div>
                <div style={{ letterSpacing: '0.3px', lineHeight: '1.3' }}>
                  {translate('deposit.pixPing.attention.description')}
                </div>
              </div>
            </StyledInformationAlert>
            <div style={{ width: '100%', textAlign: 'center' }}>
              <Button expand type="submit" disabled={isDisabled}>
                {isLoading ? (
                  <SpinnerLoader size="sm" theme="accentTertiary" />
                ) : (
                  translate('common.continue')
                )}
              </Button>
            </div>
          </div>
        </ConfirmPixStepBody>
      </form>
    </>
  )
}

ConfirmPixStep.propTypes = {
  selectedPixKey: PropTypes.number.isRequired,
  openDepositFlowFromPixPing: PropTypes.func.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  eventPixType: PropTypes.string.isRequired,
}

export default ConfirmPixStep
